import { AudiencePageLayout, type AudienceContent } from '../audience/AudiencePageLayout';

const content: Record<'de' | 'en', AudienceContent> = {
  de: {
    title: 'qtplan – Architecture Decision Records (ADRs) | Architekturentscheidungen dokumentieren',
    metaDesc: 'Architecture Decision Records (ADRs) als Tool direkt im Planungstool. Kontext, Entscheidung, Konsequenzen — mit Canvas-Integration, Status-Workflow und Relationen. Kein separates Wiki, kein Account nötig.',
    hero: {
      h1: 'Architecture Decision Records — Architekturentscheidungen nachvollziehbar machen.',
      lead: 'Warum wurde das so gebaut? Welche Alternativen gab es? Was waren die Konsequenzen? Architecture Decision Records (ADRs) in qtplan sind kein separates Wiki und keine Dokumentation um der Dokumentation willen. Sie sind ein lebendiges Werkzeug das zeigt warum Entscheidungen getroffen wurden — direkt dort wo geplant wird.',
    },
    bento: {
      heading: 'Entscheidungen festhalten, Kontext bewahren',
      items: [
        {
          span: 2,
          label: 'KONTEXT',
          title: 'Warum wurde entschieden',
          body: 'Jeder ADR beginnt mit dem Kontext — die Ausgangslage, die Rahmenbedingungen, die Anforderungen die zur Entscheidung geführt haben. Rich-Text mit BlockNote, so detailliert wie nötig. Der Kontext ist der wichtigste Teil: ohne ihn ist die Entscheidung nicht nachvollziehbar.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ENTSCHEIDUNG',
          title: 'Was wurde entschieden',
          body: 'Die Entscheidung selbst — klar formuliert, mit den Alternativen die erwogen wurden. Nicht nur das Ergebnis sondern auch der Weg dorthin.',
        },
        {
          span: 1,
          label: 'KONSEQUENZEN',
          title: 'Was folgt daraus',
          body: 'Jede Architekturentscheidung hat Konsequenzen. Positive, negative, akzeptierte Trade-offs. ADRs machen diese explizit statt sie im Nachhinein zu entdecken.',
        },
        {
          span: 1,
          label: 'STATUS-WORKFLOW',
          title: 'Draft → Proposed → Accepted',
          body: 'ADRs durchlaufen einen klaren Lebenszyklus: Draft, Proposed, Accepted, Deprecated oder Superseded. Der Status zeigt auf einen Blick welche Entscheidungen aktuell gelten und welche überholt sind.',
        },
        {
          span: 1,
          label: 'IMPLEMENTATION',
          title: 'Umsetzung nachverfolgen',
          body: 'Implementation-Tracking von Pending über In Progress bis Done oder Abandoned. Damit ist sichtbar ob eine akzeptierte Entscheidung auch tatsächlich umgesetzt wurde.',
          variant: 'accent',
        },
        {
          span: 2,
          label: 'RELATIONEN',
          title: 'ADRs verlinken sich gegenseitig',
          body: 'Supersedes, Amends, Relates-to — Architekturentscheidungen stehen nicht isoliert. Ein neuer ADR kann einen alten ersetzen, ergänzen oder darauf aufbauen. Die Relationen machen die Evolution der Architektur nachvollziehbar.',
        },
        {
          span: 2,
          label: 'CANVAS-INTEGRATION',
          title: 'Infrastruktur zeichnen, Entscheidungen verankern',
          body: 'ADRs lassen sich direkt mit dem Canvas verknüpfen. Infrastruktur-Diagramme, Systemlandschaften, Tech-Trees — an jeder relevanten Stelle einen Dot platzieren der direkt zum zugehörigen ADR führt. Warum wurde dieser Service so geschnitten? Warum dieses Protokoll? Die Antwort ist einen Klick entfernt, genau dort wo die Frage entsteht.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'SCOPE',
          title: 'Global, Board oder Canvas',
          body: 'ADRs können global gelten, an ein bestimmtes Board gebunden oder auf einen Canvas bezogen sein. Ein Canvas-spezifischer ADR erscheint als Dot direkt auf der Zeichenfläche — der Scope bestimmt wo der ADR lebt und sichtbar wird.',
        },
        {
          span: 1,
          label: 'KOMMENTARE',
          title: 'Diskussion direkt am ADR',
          body: 'Kommentare mit Resolve-Funktion direkt am ADR. Diskussionen sind Teil der Entscheidungshistorie — nicht in einem separaten Chat-Tool verstreut.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Warum Architecture Decision Records wichtig sind',
      paragraphs: [
        'Architekturentscheidungen werden vergessen. Der Kontext geht verloren. Neue Teammitglieder verstehen nicht warum etwas so gebaut wurde und bauen es anders — oder schlimmer, bauen es nochmal genauso weil sie die Probleme nicht kennen die zur ursprünglichen Entscheidung geführt haben.',
        'In der Praxis landen Architekturentscheidungen in Confluence-Seiten die niemand findet, in Slack-Threads die verschwinden oder im Kopf der Person die damals dabei war. Wenn diese Person das Team verlässt, geht der Kontext mit. Die Entscheidung steht noch im Code — aber das Warum ist weg.',
        'ADRs in qtplan sind kein separates Dokumentationssystem. Sie leben neben den Tasks, den Schätzungen und dem Canvas. Die Canvas-Integration macht das greifbar: Infrastruktur-Diagramme zeichnen, an den relevanten Stellen Dots platzieren, und jeder Dot führt direkt zum ADR der erklärt warum genau dieser Service so geschnitten wurde, warum dieses Protokoll gewählt wurde, warum diese Datenbank. Die Entscheidung ist nicht in einem Wiki versteckt sondern genau dort verankert wo die Architektur sichtbar ist. Wenn ein ADR einen anderen ersetzt, zeigt die Relation warum. Der Status-Workflow macht klar welche Entscheidungen aktuell gelten und welche durch neuere ersetzt wurden.',
        'Das Ziel ist nicht mehr Architekturdokumentation sondern bessere Nachvollziehbarkeit. Kein separates ADR-Tool, kein Template das in einem Wiki verstaubt. Ein Architecture Decision Record in qtplan kostet fünf Minuten. Die Alternative — sechs Monate später herausfinden warum etwas so gebaut wurde — kostet Tage.',
      ],
    },
    cta: {
      heading: 'Entscheidungen festhalten.',
      body: 'Kein Account. Keine Cloud. Alle Daten bleiben lokal im Browser. Board erstellen, den ersten ADR anlegen und erleben wie Architekturentscheidungen Teil des Planungsprozesses werden.',
    },
  },
  en: {
    title: 'qtplan – Architecture Decision Records (ADRs) | Context, Decision & Consequences',
    metaDesc: 'Architecture Decision Records (ADRs) as a tool built into your planning workflow. Context, decision, consequences — with canvas integration, status workflow and relations. No separate wiki, no account required.',
    hero: {
      h1: 'Architecture Decision Records — make every decision traceable.',
      lead: 'Why was it built this way? What were the alternatives? What were the consequences? Architecture Decision Records (ADRs) in qtplan are not a separate wiki and not documentation for the sake of documentation. They are a living tool that shows why decisions were made — right where planning happens.',
    },
    bento: {
      heading: 'Capture decisions, preserve context',
      items: [
        {
          span: 2,
          label: 'CONTEXT',
          title: 'Why the decision was made',
          body: 'Every ADR starts with context — the situation, the constraints, the requirements that led to the decision. Rich text with BlockNote, as detailed as needed. Context is the most important part: without it, the decision is not traceable.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'DECISION',
          title: 'What was decided',
          body: 'The decision itself — clearly stated, with the alternatives that were considered. Not just the outcome but also the path to get there.',
        },
        {
          span: 1,
          label: 'CONSEQUENCES',
          title: 'What follows from it',
          body: 'Every architecture decision has consequences. Positive, negative, accepted trade-offs. ADRs make these explicit instead of discovering them after the fact.',
        },
        {
          span: 1,
          label: 'STATUS WORKFLOW',
          title: 'Draft → Proposed → Accepted',
          body: 'ADRs follow a clear lifecycle: Draft, Proposed, Accepted, Deprecated or Superseded. The status shows at a glance which decisions are current and which have been superseded.',
        },
        {
          span: 1,
          label: 'IMPLEMENTATION',
          title: 'Track implementation',
          body: 'Implementation tracking from Pending through In Progress to Done or Abandoned. This makes visible whether an accepted decision was actually implemented.',
          variant: 'accent',
        },
        {
          span: 2,
          label: 'RELATIONS',
          title: 'ADRs link to each other',
          body: 'Supersedes, Amends, Relates-to — architecture decisions do not stand in isolation. A new ADR can replace, amend, or build upon an old one. Relations make the evolution of the architecture traceable.',
        },
        {
          span: 2,
          label: 'CANVAS INTEGRATION',
          title: 'Draw infrastructure, anchor decisions',
          body: 'ADRs link directly to the canvas. Infrastructure diagrams, system landscapes, tech trees — place a dot at any relevant point that leads straight to the corresponding ADR. Why was this service split this way? Why this protocol? The answer is one click away, right where the question arises.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'SCOPE',
          title: 'Global, Board or Canvas',
          body: 'ADRs can apply globally, be tied to a specific board, or reference a canvas. A canvas-scoped ADR appears as a dot directly on the drawing surface — the scope determines where the ADR lives and becomes visible.',
        },
        {
          span: 1,
          label: 'COMMENTS',
          title: 'Discussion right on the ADR',
          body: 'Comments with resolve functionality directly on the ADR. Discussions are part of the decision history — not scattered across a separate chat tool.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Why Architecture Decision Records matter',
      paragraphs: [
        'Architecture decisions get forgotten. Context is lost. New team members do not understand why something was built the way it was and build it differently — or worse, build it the same way again because they do not know the problems that led to the original decision.',
        'In practice, architecture decisions end up in Confluence pages that nobody finds, in Slack threads that disappear, or in the head of the person who was there at the time. When that person leaves the team, the context leaves with them. The decision is still in the code — but the why is gone.',
        'ADRs in qtplan are not a separate documentation system. They live alongside tasks, estimates, and the canvas. The canvas integration makes this tangible: draw infrastructure diagrams, place dots at relevant points, and each dot leads directly to the ADR that explains why exactly this service was split this way, why this protocol was chosen, why this database. The decision is not hidden in a wiki but anchored right where the architecture is visible. When an ADR supersedes another, the relation shows why. The status workflow makes clear which decisions are current and which have been replaced by newer ones.',
        'The goal is not more software architecture documentation but better traceability. No separate ADR tool, no template gathering dust in a wiki. An Architecture Decision Record in qtplan takes five minutes. The alternative — figuring out six months later why something was built that way — takes days.',
      ],
    },
    cta: {
      heading: 'Capture decisions.',
      body: 'No account. No cloud. All data stays local in the browser. Create a board, write the first ADR, and experience how architecture decisions become part of the planning process.',
    },
  },
};

export function AdrFeaturePage({ lang }: { lang: 'de' | 'en' }) {
  return <AudiencePageLayout lang={lang} slug="adrs" content={content[lang]} basePath="features" />;
}
